import { useCallback, useEffect, useState } from "react";
import type RTKClient from "@cloudflare/realtimekit";
import {
  LIVESTREAM_RANDOM_PARTICIPANT_MESSAGE_TYPE,
  buildLivestreamRandomParticipantPayload,
  generateLivestreamRandomParticipantDrawId,
  parseLivestreamRandomParticipantDraw,
  type LivestreamRandomParticipantDraw,
} from "./livestreamRandomParticipant";

export function useLivestreamRandomParticipant({
  meeting,
}: {
  meeting: RTKClient | undefined;
}) {
  const [activeDraw, setActiveDraw] = useState<LivestreamRandomParticipantDraw | null>(null);

  useEffect(() => {
    if (!meeting?.participants) return;

    const onBroadcastedMessage = ({
      type,
      payload,
    }: {
      type: string;
      payload: Record<string, boolean | number | string | Date>;
    }) => {
      if (type !== LIVESTREAM_RANDOM_PARTICIPANT_MESSAGE_TYPE) return;
      const draw = parseLivestreamRandomParticipantDraw({ payload });
      if (!draw) return;
      setActiveDraw((current) => (current?.id === draw.id ? current : draw));
    };

    meeting.participants.on("broadcastedMessage", onBroadcastedMessage);
    return () => {
      meeting.participants.off("broadcastedMessage", onBroadcastedMessage);
    };
  }, [meeting]);

  const selectRandomParticipant = useCallback(
    async ({
      eligibleNames,
    }: {
      eligibleNames: string[];
    }) => {
      if (!meeting?.participants || eligibleNames.length === 0) return;

      const winner = eligibleNames[Math.floor(Math.random() * eligibleNames.length)]!;
      const draw: LivestreamRandomParticipantDraw = {
        id: generateLivestreamRandomParticipantDrawId(),
        winner,
        names: eligibleNames,
      };

      setActiveDraw(draw);
      await meeting.participants.broadcastMessage(
        LIVESTREAM_RANDOM_PARTICIPANT_MESSAGE_TYPE,
        buildLivestreamRandomParticipantPayload({ draw }),
      );
    },
    [meeting],
  );

  const clearActiveDraw = useCallback(() => {
    setActiveDraw(null);
  }, []);

  return {
    activeDraw,
    selectRandomParticipant,
    clearActiveDraw,
  };
}
